import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as Tone from 'tone';
import Confetti from 'react-confetti';
import './game.css'; // Using the enhanced CSS

// --- ICONS & SETTINGS ---
const CARD_ICONS = [
    '⭐', '🚀', '💎', '💡', '🏆', '🌈', '⚡', '👑', '🔑', '❤️', '🍀', '🍎', '🍓', '🍋', '�', '🍌',
    '🔔', '🌸', '🌵', '🧩', '🎸', '🎨', '📚', '🗺️', '🎶', '🌍', '⏰', '🛡️', '⚙️', '📈', '📊', '🔗'
];

const DIFFICULTY_SETTINGS = {
    easy: { pairs: 6, peekTime: 2000, flipDelay: 1000, message: "A great place to start. Take a good look!" },
    medium: { pairs: 10, peekTime: 1500, flipDelay: 800, message: "A balanced test for your visual memory." },
    hard: { pairs: 16, peekTime: 1000, flipDelay: 600, message: "Quick eyes and a sharp mind needed!" }
};

// --- SOUND EFFECTS (Synthesized with Tone.js) ---
const sounds = {
    flip: new Tone.Synth({ oscillator: { type: 'sine' }, envelope: { attack: 0.005, decay: 0.1, sustain: 0.3, release: 1 } }).toDestination(),
    match: new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.05, decay: 0.2, sustain: 0.2, release: 0.8 } }).toDestination(),
    noMatch: new Tone.Synth({ oscillator: { type: 'square' }, envelope: { attack: 0.05, decay: 0.2, sustain: 0, release: 0.5 } }).toDestination(),
    win: new Tone.GrainPlayer("https://tonejs.github.io/audio/berklee/gong_1.mp3").toDestination() // Using a sample for a better win sound
};
sounds.win.volume.value = -6;


const MemoryMatch = () => {
    // --- STATE MANAGEMENT ---
    const [cards, setCards] = useState([]);
    const [flippedIndices, setFlippedIndices] = useState([]);
    const [matchedIds, setMatchedIds] = useState([]);
    const [moves, setMoves] = useState(0);
    const [time, setTime] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [gameStatus, setGameStatus] = useState('menu'); // 'menu', 'peeking', 'playing', 'won'
    const [difficulty, setDifficulty] = useState('medium');
    const [highScores, setHighScores] = useState(() => {
        try {
            const savedScores = localStorage.getItem('memoryMatchHighScores');
            return savedScores ? JSON.parse(savedScores) : { easy: null, medium: null, hard: null };
        } catch (error) {
            console.error("Could not load high scores", error);
            return { easy: null, medium: null, hard: null };
        }
    });
    const [isShaking, setIsShaking] = useState(false);
    const [theme, setTheme] = useState('light');


    // --- CORE GAME LOGIC ---
    const shuffleArray = useCallback((array) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }, []);

    const initializeBoard = useCallback(() => {
        const settings = DIFFICULTY_SETTINGS[difficulty];
        const availableIcons = CARD_ICONS.slice(0, settings.pairs);
        const initialCards = availableIcons.flatMap((icon, index) => [
            { id: `${index}-a`, value: icon }, { id: `${index}-b`, value: icon }
        ]);

        setCards(shuffleArray(initialCards));
        setFlippedIndices([]);
        setMatchedIds([]);
        setMoves(0);
        setTime(0);
        setIsProcessing(true);
        setGameStatus('peeking');
    }, [difficulty, shuffleArray]);


    // --- USEEFFECT HOOKS FOR GAME FLOW & FEATURES ---

    // Theme toggler
    useEffect(() => {
        document.body.setAttribute('data-theme', theme);
    }, [theme]);

    // Transition from 'peeking' to 'playing'
    useEffect(() => {
        if (gameStatus !== 'peeking') return;
        const peekTimer = setTimeout(() => {
            setGameStatus('playing');
            setIsProcessing(false);
        }, DIFFICULTY_SETTINGS[difficulty].peekTime);
        return () => clearTimeout(peekTimer);
    }, [gameStatus, difficulty]);

    // Game timer
    useEffect(() => {
        if (gameStatus !== 'playing') return;
        const gameTimer = setInterval(() => setTime(t => t + 1), 1000);
        return () => clearInterval(gameTimer);
    }, [gameStatus]);

    // Check for match when two cards are flipped
    useEffect(() => {
        if (flippedIndices.length !== 2) return;

        setIsProcessing(true);
        setMoves(prevMoves => prevMoves + 1);

        const [firstIndex, secondIndex] = flippedIndices;
        const firstCard = cards[firstIndex];
        const secondCard = cards[secondIndex];

        if (firstCard.value === secondCard.value) {
            // MATCH FOUND
            sounds.match.triggerAttackRelease("C5", "8n", Tone.now() + 0.1);
            setMatchedIds(prev => [...prev, firstCard.id, secondCard.id]);
            setFlippedIndices([]);
            setIsProcessing(false);
        } else {
            // NO MATCH
            sounds.noMatch.triggerAttackRelease("C3", "8n", Tone.now() + 0.1);
            setIsShaking(true);
            const timer = setTimeout(() => {
                setFlippedIndices([]);
                setIsProcessing(false);
                setIsShaking(false);
            }, DIFFICULTY_SETTINGS[difficulty].flipDelay);
            return () => clearTimeout(timer);
        }
    }, [flippedIndices, cards, difficulty]);

    // Check for win condition and update high scores
    useEffect(() => {
        if (gameStatus === 'playing' && cards.length > 0 && matchedIds.length === cards.length) {
            setGameStatus('won');
            sounds.win.start();
            
            const currentBest = highScores[difficulty];
            if (currentBest === null || moves < currentBest) {
                const newHighScores = { ...highScores, [difficulty]: moves };
                setHighScores(newHighScores);
                localStorage.setItem('memoryMatchHighScores', JSON.stringify(newHighScores));
            }
        }
    }, [matchedIds, cards.length, gameStatus, difficulty, moves, highScores]);

    // --- EVENT HANDLERS & HELPERS ---
    const handleCardClick = (clickedIndex) => {
        if (Tone.context.state !== 'running') {
            Tone.start();
        }
        if (isProcessing || flippedIndices.includes(clickedIndex) || matchedIds.includes(cards[clickedIndex].id)) {
            return;
        }
        sounds.flip.triggerAttackRelease("C4", "8n");
        setFlippedIndices(prev => [...prev, clickedIndex]);
    };
    
    const handleReturnToMenu = () => {
        setGameStatus('menu');
        setCards([]);
    };
    
    const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    const getGridStyle = useCallback(() => ({
        gridTemplateColumns: `repeat(${difficulty === 'easy' ? 4 : difficulty === 'medium' ? 5 : 8}, 1fr)`
    }), [difficulty]);


    // --- RENDER FUNCTIONS FOR GAME STATES ---
    const renderMenu = () => (
        <div className="game-screen">
            <h2 className="screen-title">Select Difficulty</h2>
            <div className="difficulty-options">
                {Object.keys(DIFFICULTY_SETTINGS).map(level => (
                    <button key={level} className={`difficulty-button ${difficulty === level ? 'active' : ''}`} onClick={() => setDifficulty(level)}>
                        {level.toUpperCase()}
                    </button>
                ))}
            </div>
            <p className="difficulty-description">{DIFFICULTY_SETTINGS[difficulty].message}</p>
            {highScores[difficulty] && <p className="high-score-display">Best Score: <strong>{highScores[difficulty]} moves</strong></p>}
            <button className="primary-button" onClick={initializeBoard}>Start Game</button>
        </div>
    );

    const renderGame = () => (
        <>
            <div className="game-info-bar">
                <span>Moves: <strong className="info-value">{moves}</strong></span>
                <div className="theme-switcher" onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}>
                    <span role="img" aria-label="sun">☀️</span> / <span role="img" aria-label="moon">🌙</span>
                </div>
                <span>Time: <strong className="info-value">{formatTime(time)}</strong></span>
                <button className="secondary-button" onClick={handleReturnToMenu}>New Game</button>
            </div>
        <div className={`card-grid ${isShaking ? 'shake' : ''}`} style={getGridStyle()}>
    {cards.map((card, index) => {
        const isMatched = matchedIds.includes(card.id);
        const isFlipped = gameStatus === 'peeking' || flippedIndices.includes(index);

        return (
            <div key={card.id} className="card-container">
                <button 
                    className={`card-inner ${isMatched ? 'is-matched' : (isFlipped ? 'is-flipped' : '')}`} 
                    onClick={() => handleCardClick(index)} 
                    disabled={isProcessing || isMatched}
                >
                    <div className="card-face card-back">
                        {isMatched ? '' : '?'}
                    </div>

                    <div className="card-face card-front">
                        {isMatched ? '❌' : card.value}
                    </div>
                </button>
            </div>
        );
    })}
</div>

        </>
    );

    const renderWinScreen = () => (
        <div className="game-screen">
            <Confetti recycle={false} numberOfPieces={300} />
            <h2 className="screen-title">🎉 Congratulations! 🎉</h2>
            <p className="win-message">You matched all pairs!</p>
            <div className="win-stats">
                <p><strong>Moves:</strong> {moves}</p>
                <p><strong>Time:</strong> {formatTime(time)}</p>
                {highScores[difficulty] === moves && <p className="new-high-score">🏆 New Best Score!</p>}
            </div>
            <button className="primary-button" onClick={handleReturnToMenu}>Play Again</button>
        </div>
    );

    return (
        <div className="memory-match-container">
            <h1 className="game-title">Memory Match</h1>
            <div className="game-board">
                {gameStatus === 'menu' && renderMenu()}
                {(gameStatus === 'peeking' || gameStatus === 'playing') && renderGame()}
                {gameStatus === 'won' && renderWinScreen()}
            </div>
        </div>
    );
};

export default MemoryMatch;
